import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getContacts, markContactRead, deleteContact } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Trash2, Mail, Phone, Check, Circle } from 'lucide-react';
import { format } from 'date-fns';

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const res = await getContacts();
      setContacts(res.data);
    } catch (err) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markContactRead(id);
      toast.success('Marked as read');
      loadContacts();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await deleteContact(id);
      toast.success('Message deleted');
      loadContacts();
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  const filteredContacts = filter === 'all' 
    ? contacts 
    : filter === 'unread' 
      ? contacts.filter(c => !c.read)
      : contacts.filter(c => c.read);

  const unreadCount = contacts.filter(c => !c.read).length;

  return (
    <AdminLayout title="Contact Messages">
      <div className="space-y-6" data-testid="admin-contacts">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">
              Messages
              {unreadCount > 0 && (
                <span className="ml-3 bg-[#ff5a1f] text-white text-sm px-2 py-1 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </h1>
            <p className="text-[#888] text-sm">Contact form submissions</p>
          </div>
          
          {/* Filter */}
          <div className="flex gap-2">
            {['all', 'unread', 'read'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 text-xs font-medium capitalize transition-colors ${
                  filter === status
                    ? 'bg-[#ff5a1f] text-white'
                    : 'bg-[#121212] text-[#888] hover:text-white border border-[#333]'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Messages List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
          </div>
        ) : filteredContacts.length > 0 ? (
          <div className="space-y-4">
            {filteredContacts.map((contact) => (
              <div
                key={contact.id}
                className={`bg-[#0A0A0A] border p-6 ${
                  contact.read ? 'border-[#1F1F1F]' : 'border-[#ff5a1f]/50'
                }`}
                data-testid={`contact-${contact.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  {/* Info */}
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center gap-3">
                      {contact.read ? (
                        <Check className="w-4 h-4 text-[#555]" />
                      ) : (
                        <Circle className="w-4 h-4 text-[#ff5a1f] fill-[#ff5a1f]" />
                      )}
                      <h3 className="font-syne font-bold text-white">{contact.name}</h3>
                      <span className="text-[#555] text-xs">
                        {contact.created_at && format(new Date(contact.created_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-[#888]">
                      <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-[#ff5a1f]">
                        <Mail className="w-4 h-4" />
                        {contact.email}
                      </a>
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-[#ff5a1f]">
                          <Phone className="w-4 h-4" />
                          {contact.phone}
                        </a>
                      )}
                    </div>
                    
                    <h4 className="text-white font-medium">{contact.subject}</h4>
                    <p className="text-[#888] text-sm bg-[#121212] p-4 border-l-2 border-[#333] whitespace-pre-wrap">
                      {contact.message}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 lg:flex-col lg:min-w-[120px]">
                    {!contact.read && (
                      <button
                        onClick={() => handleMarkRead(contact.id)}
                        className="flex-1 border border-[#333] text-white px-4 py-2 text-sm flex items-center justify-center gap-2 hover:border-[#ff5a1f] hover:text-[#ff5a1f]"
                      >
                        <Check className="w-4 h-4" />
                        Mark Read
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(contact.id)}
                      className="border border-[#333] text-red-500 px-4 py-2 text-sm flex items-center justify-center gap-2 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-16 text-center">
            <p className="text-[#888]">
              {filter === 'all' ? 'No messages yet' : `No ${filter} messages`}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
